import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  TablePagination,
  TableSortLabel,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Search,
  Refresh,
  PlaylistAddCheck,
  DocumentScanner,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Visibility,
  Edit,
  CallMerge,
  Delete,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import useShipments from "../../hooks/useShipments";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import BulkTrackingStatusDialog from "../../components/shipments/BulkTrackingStatusDialog";
import ScanUnclaimedMatchesDialog from "../../components/common/ScanUnclaimedMatchesDialog";
import ConsolidationPickerDialog from "../../components/common/ConsolidationPickerDialog";
import AnalyzeWithAiButton from "../../components/common/AnalyzeWithAiButton";
import NewShipmentSplitButton from "../../components/common/NewShipmentSplitButton";
import { selectStatusOptions, selectKeyForRole, selectStatusOrder } from "../../redux/slices/statusDefinitionsSlice";
import { formatUsd } from "../../utils/currency";
import { slugifyTab } from "../../utils/tabSlug";

// Module-level (not inline in the component) so useUrlState gets a stable
// reference every render — see that hook's own doc comment for why.
const URL_SCHEMA = {
  // The tab's slugified label ("all", "awaiting_invoice", …) rather than
  // its array index — stable if tabs are ever reordered, and readable in
  // the address bar/shared links instead of a meaningless number.
  tab: { default: "" },
  search: { default: "" },
  carrier: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
  rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
  sortBy: { default: null },
  sortOrder: { default: null },
};

// Same carrier values offered at shipment-creation time (ShipmentForm.jsx's
// own CARRIER_OPTIONS, not exported from there — kept local here rather
// than adding a cross-file dependency for a 6-item list), minus that one's
// blank "Select carrier…" placeholder — "All Carriers" below covers that
// case here instead.
const CARRIER_FILTER_OPTIONS = [
  { value: "", label: "All Carriers" },
  { value: "UPS", label: "UPS" },
  { value: "FEDEX", label: "FedEx" },
  { value: "USPS", label: "USPS" },
  { value: "DHL", label: "DHL" },
  { value: "AMAZON", label: "Amazon Logistics" },
  { value: "OTHER", label: "Other" },
];

// Sum of each ITEM's own weight/cbf (set once a dock receipt is entered,
// a carrier reports it, or an admin overrides it manually — see
// Shipment.js's item schema) rather than Shipment.totalWeight/totalCbf,
// which sum dockReceipts[] instead. The two normally agree, but an item
// not yet synced to its D/R split (or one dropped from a D/R) can make
// them differ — this column is specifically "what the items on this
// shipment carry," so it reads straight off the items.
const sumItemMeasurements = (items) => {
  let weight = 0;
  let cbf = 0;
  let any = false;
  (items || []).forEach((item) => {
    if (item.weight != null) {
      weight += item.weight;
      any = true;
    }
    // displayCbf applies the admin-configured minimum-CBF floor — this
    // list is pure display (no editable CBF field here), so the floored
    // value is the right one to show/sum.
    const itemCbf = item.displayCbf ?? item.cbf;
    if (itemCbf != null) {
      cbf += itemCbf;
      any = true;
    }
  });
  return any ? { weight: Math.round(weight * 100) / 100, cbf: Math.round(cbf * 1000) / 1000 } : { weight: null, cbf: null };
};

const truncateWords = (text, maxWords = 3) => {
  if (!text) return "—";
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(" ")}...`;
};

const AdminShipments = () => {
  const navigate = useNavigate();
  const { shipments, pagination, loading, load, syncTracking, update, remove } =
    useShipments();

  const statusOptions = useSelector(selectStatusOptions("shipment"));
  const itemReceivedKey = useSelector(selectKeyForRole("item", "received"));
  const itemStatusOrder = useSelector(selectStatusOrder("item"));
  const consolidationCompletedKey = useSelector(selectKeyForRole("consolidation", "completed"));
  const receivedIdx = itemStatusOrder.indexOf(itemReceivedKey);
  const hasConsolidationEligibleItem = (s) =>
    s.items?.some((item) => {
      if (item.consolidation) return false;
      const idx = item.itemStatus ? itemStatusOrder.indexOf(item.itemStatus) : 0;
      return receivedIdx === -1 || idx <= receivedIdx;
    });
  // Distinct consolidations touching this shipment — a shipment's items
  // normally all land in the SAME consolidation (see Consolidation.js's own
  // comment), but this stays a de-duped list rather than assuming exactly
  // one, in case that ever isn't true. Nothing in the shipments TABLE
  // otherwise showed this at a glance — an admin had to open the shipment
  // to discover it was mid-consolidation.
  const getShipmentConsolidations = (s) => {
    const byId = new Map();
    (s.items || []).forEach((item) => {
      if (item.consolidation?._id) byId.set(item.consolidation._id, item.consolidation);
    });
    return [...byId.values()];
  };
  const TABS = [{ label: "All", value: "" }, ...statusOptions];

  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const { tab, carrier, page, rowsPerPage, sortBy, sortOrder } = urlState;
  const activeTab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === tab));
  // Local typing buffer for the search box — kept separate from the URL so
  // every keystroke doesn't rewrite the address bar; only committed to
  // urlState (and actually re-queried) on Enter, same as before. Seeded
  // from the URL on mount so a restored search still shows in the field.
  const [search, setSearch] = useState(urlState.search);
  const [syncingId, setSyncingId] = useState(null);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [consolidationPicker, setConsolidationPicker] = useState(null);

  const doLoad = (overrides = {}) => {
    load({
      status: TABS[activeTab].value || undefined,
      // "All" tab (index 0, empty value) means "everything in progress" —
      // delivered shipments only show under their own Delivered tab.
      excludeDelivered: activeTab === 0 ? true : undefined,
      search: search || undefined,
      carrier: carrier || undefined,
      page: page + 1,
      limit: rowsPerPage,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      ...overrides,
    });
  };

  useEffect(() => {
    doLoad();
  }, [activeTab, carrier, page, rowsPerPage, sortBy, sortOrder]);

  // Tracking #, Status, Created — the only columns worth sorting by.
  // Clicking the already-active column flips its direction; clicking a
  // different one switches to it, starting ascending.
  const handleSort = (column) => {
    if (sortBy === column) {
      setUrlState({ sortOrder: sortOrder === "asc" ? "desc" : "asc", page: 0 });
    } else {
      setUrlState({ sortBy: column, sortOrder: "asc", page: 0 });
    }
  };

  const sortableHeaderCell = (column, label, align) => (
    <TableCell align={align}>
      <TableSortLabel
        active={sortBy === column}
        direction={sortBy === column ? sortOrder : "asc"}
        onClick={() => handleSort(column)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setUrlState({ search: e.target.value, page: 0 });
      doLoad({ page: 1, search: e.target.value });
    }
  };

  const handleSync = async (e, id) => {
    e.stopPropagation();

    try {
      setSyncingId(id);
      await syncTracking(id);
      await doLoad();
    } catch (err) {
      console.error(err);
    } finally {
      setSyncingId(null);
    }
  };

  const handleDelete = async (e, s) => {
    e.stopPropagation();
    if (!window.confirm(`Delete shipment ${s.trackingNumber}? This cannot be undone.`)) return;
    const ok = await remove(s._id);
    if (ok) doLoad();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Shipments"
        subtitle={`${pagination.total || 0} total shipments`}
        action={
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<PlaylistAddCheck />}
              onClick={() => setBulkStatusOpen(true)}
            >
              Bulk Update Status
            </Button>
            <Tooltip title="Re-check every tracking number on undelivered shipments against unclaimed packages">
              <Button
                variant="outlined"
                startIcon={<DocumentScanner />}
                onClick={() => setScanDialogOpen(true)}
              >
                Scan for Matches
              </Button>
            </Tooltip>
            <AnalyzeWithAiButton />
            <NewShipmentSplitButton
              size="small"
              onNewShipment={() => navigate("/admin/shipments/add")}
              canBulkAdd
              onBulkAdd={() => navigate("/admin/shipments/bulk-add")}
              canBulkImport
              onBulkImport={() => navigate("/admin/shipments/bulk-import")}
            />
          </Box>
        }
      />

      <Card>
        <Box
          sx={{
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setUrlState({ tab: slugifyTab(TABS[v].label), page: 0 })}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              maxWidth: "100%",
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={i} label={t.label} />
            ))}
          </Tabs>
          <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1, pb: 1 }}>
            <TextField
              select
              size="small"
              label="Carrier"
              value={carrier}
              onChange={(e) => setUrlState({ carrier: e.target.value, page: 0 })}
              sx={{ width: { xs: "100%", sm: 150 } }}
            >
              {CARRIER_FILTER_OPTIONS.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="Search tracking, order #, customer, item…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearch}
              sx={{ width: { xs: "100%", sm: 260 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => doLoad()}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <>
          {loading ? (
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {sortableHeaderCell("trackingNumber", "Tracking #")}
                    <TableCell>Customer</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Weight / CBF</TableCell>
                    <TableCell align="right">Invoice Total</TableCell>
                    {sortableHeaderCell("status", "Status")}
                    {sortableHeaderCell("createdAt", "Created")}
                    <TableCell align="center" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ p: 0 }}>
                      <SkeletonLoader rows={Math.min(8, rowsPerPage)} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          ) : shipments.length === 0 ? (
            <EmptyState
              icon="📦"
              title="No shipments found"
              description="Try adjusting your filters or create a new shipment."
              actionLabel="New Shipment"
              onAction={() => navigate("/admin/shipments/add")}
            />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {sortableHeaderCell("trackingNumber", "Tracking #")}
                    <TableCell>Customer</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Weight / CBF</TableCell>
                    <TableCell align="right">Invoice Total</TableCell>
                    {sortableHeaderCell("status", "Status")}
                    {sortableHeaderCell("createdAt", "Created")}
                    <TableCell align="center" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {shipments.map((s) => {
                    const isExpanded = expandedId === s._id;
                    // A search can match a shipment purely because one of its
                    // ITEMS' carrier tracking numbers matched, not the
                    // shipment's own tracking number — in that case, jump
                    // straight to that item on the detail page instead of
                    // just the shipment, since that's what the admin was
                    // actually looking for.
                    const matchedItem = search
                        ? (s.items || []).find((it) =>
                              (it.trackingNumber || "").toLowerCase().includes(search.toLowerCase()),
                          )
                        : null;
                    const goToShipment = () =>
                        navigate(
                            matchedItem
                                ? `/admin/shipments/${s.trackingNumber}?item=${matchedItem._id}`
                                : `/admin/shipments/${s.trackingNumber}`,
                        );
                    return (
                    <React.Fragment key={s._id}>
                    <TableRow
                      onClick={goToShipment}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.trackingNumber}
                      </TableCell>

                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        <Tooltip title={`${s.customer?.firstName || ""} ${s.customer?.lastName || ""}`.trim()}>
                          <span>{truncateWords(`${s.customer?.firstName || ""} ${s.customer?.lastName || ""}`.trim(), 2)}</span>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={s.packageDescription || ""}>
                          <Typography noWrap variant="body2" color="text.secondary">
                            {truncateWords(s.packageDescription, 3)}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell align="right">{s.items?.length || 0}</TableCell>

                      {(() => {
                        const { weight, cbf } = sumItemMeasurements(s.items);
                        return (
                          <TableCell
                            align="right"
                            sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}
                          >
                            {weight != null || cbf != null
                              ? `${weight != null ? `${weight} lbs` : "—"} / ${cbf != null ? `${cbf} ft³` : "—"}`
                              : "N/A"}
                          </TableCell>
                        );
                      })()}

                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                      >
                        {formatUsd(s.invoiceTotal)}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                          <StatusChip status={s.status} domain="shipment" />
                          {getShipmentConsolidations(s).map((c) => (
                            <Tooltip
                              key={c._id}
                              title={
                                c.status === consolidationCompletedKey
                                  ? "This consolidation has completed"
                                  : "Part of a consolidation that hasn't completed yet"
                              }
                            >
                              <Chip
                                size="small"
                                icon={<CallMerge sx={{ fontSize: 13 }} />}
                                label={c.consolidationNumber}
                                variant="outlined"
                                color={c.status === consolidationCompletedKey ? "default" : "warning"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate("/admin/consolidations");
                                }}
                                sx={{ fontSize: 10.5, height: 20 }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {dayjs(s.createdAt).format("MMM D, YYYY")}
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedId(isExpanded ? null : s._id);
                          }}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUp fontSize="small" />
                          ) : (
                            <KeyboardArrowDown fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 1.5 }}>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={goToShipment}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Edit />}
                              onClick={() => navigate(`/admin/shipments/${s.trackingNumber}/edit`)}
                            >
                              Edit
                            </Button>
                            {hasConsolidationEligibleItem(s) && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CallMerge />}
                                onClick={() => setConsolidationPicker({ shipment: s })}
                              >
                                Add to Consolidation
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => handleSync(e, s._id)}
                              disabled={syncingId === s._id}
                            >
                              {syncingId === s._id ? (
                                <CircularProgress size={16} />
                              ) : (
                                "Sync Tracking"
                              )}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={(e) => handleDelete(e, s)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={(_, p) => setUrlState({ page: p })}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: +e.target.value, page: 0 })}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        </>
      </Card>

      <BulkTrackingStatusDialog
        open={bulkStatusOpen}
        onClose={() => setBulkStatusOpen(false)}
        onUpdated={() => doLoad()}
      />
      <ScanUnclaimedMatchesDialog
        open={scanDialogOpen}
        onClose={() => setScanDialogOpen(false)}
        onComplete={() => doLoad()}
      />
      <ConsolidationPickerDialog
        open={!!consolidationPicker}
        onClose={() => setConsolidationPicker(null)}
        mode="shipment"
        shipmentId={consolidationPicker?.shipment?._id}
        customerId={
          consolidationPicker?.shipment?.customer?._id ||
          consolidationPicker?.shipment?.customer
        }
        onDone={() => doLoad()}
      />
    </Box>
  );
};

export default AdminShipments;
